import { Injectable, Injector } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap, tap, finalize } from 'rxjs/operators';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { SessionService } from './session.service';
import { ToasterService } from '@src/app/shared/components/toaster/toaster.service';

@Injectable({
  providedIn: 'root'
})
export class AuthInterceptor implements HttpInterceptor {

  constructor(private injector: Injector,
              private router: Router) { }

  // Lazy-loaded getters to avoid circular DI
  private get session() {
    return this.injector.get(SessionService);
  }

  private get toasterService() {
    return this.injector.get(ToasterService);
  }

  private anonymousRouteWithoutBearer(url: string): boolean {
    const excludedPatterns = [
      /\/new-password-challenge$/,
      /\/WarrantyCard$/
    ];
    return excludedPatterns.some(pattern => pattern.test(url));
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const session = this.session;

    if (request.url.includes('/refresh') == false) {
      session.setLoading(true);
    }

    const isAuthCall =  request.url.includes('/login');

    if (isAuthCall) {

      const authEndpointsRequiringHeaderToken = ['/refresh', '/logout'];
      const needsAuthHeader = authEndpointsRequiringHeaderToken.some(endpoint => request.url.includes(endpoint));

      if (needsAuthHeader) {
        const token = localStorage.getItem('refreshToken') || localStorage.getItem('accessToken');

        if (token) {
          request = request.clone({
            headers: request.headers.set('Authorization', `Bearer ${token}`)
          });
        }
      }

      return next.handle(request).pipe(finalize(() => session.setLoading(false)));
    }

    const tokenObj = session?.session?.tokens?.apiToken;

    if (!this.anonymousRouteWithoutBearer(request.url) && tokenObj?.token) {
      try {
         request = request.clone({
            headers: request.headers.set('Authorization', `Bearer ${tokenObj.token}`),
          });
      } catch (e) {
        console.warn('Failed to decode token:', e);
      }
    }

    return next.handle(request).pipe(
      tap({
        next: (event) => {
          if (event instanceof HttpResponse) {
            this.handleSuccess(request);
          }
        }
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error, request, next)),
      finalize(() => {
        session.setLoading(false);
      })
    );
  }

  private handleSuccess(request: HttpRequest<any>): void {
    const url = request.url;

    if (url.includes('/Warranties/activate')) {
      this.toasterService.show('success', 'Information', 'Warranty activated correctly');
      return;
    }

    if (url.includes('/Warranties/disassociate')) {
      this.toasterService.show('success', 'Information', 'Warranty disassociated correctly');
      return;
    }

    if (url.includes('/upsert')) {
      this.toasterService.show('success', 'Information', 'Element saved correctly');
      return;
    }

    if (url.includes('/update')) {
      this.toasterService.show('success', 'Information', 'Element saved correctly');
      return;
    }

    if (url.includes('/create')) {
      this.toasterService.show('success', 'Information', 'Element created correctly');
      return;
    }

    if (url.includes('/deactivate')) {
      this.toasterService.show('success', 'Information', 'Element deactivated correctly');
      return;
    }

    if (url.includes('/activate')) {
      this.toasterService.show('success', 'Information', 'Element activated correctly');
      return;
    }

    if (url.includes('/delete') && !url.includes('/delete-check')) {
      this.toasterService.show('success', 'Information', 'Element deleted correctly');
      return;
    }

    if (url.includes('/add')) {
      this.toasterService.show('success', 'Information', 'Element added correctly');
      return;
    }

    if (url.includes('/set-as-default-brand')) {
      this.toasterService.show('success', 'Information', 'Brand set as default correctly');
      return;
    }

    if (url.includes('/invalidate')) {
      this.toasterService.show('success', 'Information', 'Inventory invalidated correctly');
      return;
    }
  }

  private handleError(error: HttpErrorResponse, request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    const message = this.getErrorMessage(error);

    if (error?.error?.code == "40302") {
      this.session.logout();
      this.router.navigate(['/disabled']);
    }

    return throwError(() => error);
  }

  private getErrorMessage(error: HttpErrorResponse): string {
    switch (error.status) {
      case 0:
        return 'Error connecting to the server. Please try refreshing the page.';
      case 400:
        return 'Bad Request. Please check your input and try again.';
      case 403:
        return 'Access Denied. You do not have permission to perform this action.';
      case 404:
        return 'Element not found.';
      case 500:
        return 'Internal Server Error. Please try again later.';
      default:
        return error.message || 'An unknown error occurred. Please try again.';
    }
  }
}
